import {AfterViewInit, Component, EventEmitter, OnDestroy, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {BaseView} from "../../../../../core/view/base.view";
import {ButtonModule} from "../../../../../core/component/button/button.module";
import {ErrorTextModule} from "../../../../../core/component/error-text/error-text.module";
import {MaterializeAgent} from "../../agent/materializeAgent";
import {RandomUtils} from "../../../../../../shared/utils/random.utils";
import {CardModule} from "../../../../../core/component/card/card.module";
import {Subscription} from "rxjs";
import {
  MaterializePlateFootRawData, MaterializePlateFrame,
  MaterializePlateSummary
} from "../../../impl/agent/materializeAgentPlate";
import {BlobHelper} from "../../../../../core/blob/blob.helper";
import {
  MaterializePlateExamDynamicFootComparisonDialogComponent,
  MaterializePlateExamDynamicFootComparisonDialogInput,
  MaterializePlateExamDynamicFootComparisonDialogOutput
} from "../../../impl/component/materialize-plate-exam-dynamic-foot-comparison-dialog/materialize-plate-exam-dynamic-foot-comparison-dialog.component";
import {DialogController} from "../../../../../common/dialog/dialogController";
import {PressureGravityCenter} from "../../../impl/control/materializePlateExam.control";
import {
    ConfirmationButtonModule
} from "../../../../../../components/common/button/confirmation-button/confirmation-button.module";
import {
  ConfirmationButtonOptionModel
} from "../../../../../../components/common/button/confirmation-button/model/confirmationButtonOption.model";
import {LoadingModule} from "../../../../../../components/common/loading/loading.module";
import {DrawingOnImageChangedEvent} from "../../../../../common/drawing/drawing-on-image/drawing-on-image.component";

@Component({
  selector: 'app-materialize-plate-exam-dynamic',
  templateUrl: './materialize-plate-exam-dynamic.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ErrorTextModule,
    CardModule,
    MaterializePlateExamDynamicFootComparisonDialogComponent,
    ConfirmationButtonModule,
    LoadingModule,
  ],
  providers: [
    MaterializeAgent
  ]
})
export class MaterializePlateExamDynamicComponent extends BaseView implements AfterViewInit, OnDestroy {
  confirmationButton: ConfirmationButtonOptionModel;
  recordingName: string;
  allFeet: MaterializePlateExamDynamicComponentFoot[] = [];
  plate: MaterializePlateSummary;

  subscriptions: Subscription[] = [];
  @Output() changedEvent: EventEmitter<MaterializePlateExamDynamicComponentData> = new EventEmitter<MaterializePlateExamDynamicComponentData>();

  constructor(public materializeAgent: MaterializeAgent,
              private dialogController: DialogController) {
    super();
    this.confirmationButton = new ConfirmationButtonOptionModel();
    this.confirmationButton.confirmationText = 'Après l\'arrêt de l\'enregistrement, il ne vous sera plus possible de visualiser le détail des empreintes.'
  }

  ngAfterViewInit() {

    this.subscriptions.push(this.materializeAgent.plateFootFound$.subscribe(value => {
      if (value) {
        // console.log(['foot common', value.common]);
        BlobHelper.blobToBase64(value.picture).then((base64) => {
          this.allFeet.push({
            type: value.common.type,
            selected: false,
            picture: base64,
            durationInMillis: value.common.durationInMillis,
            recordingName: value.common.measurementName,
            plateId: value.common.plate.id,
            index: value.common.index,
            frames: [],
            framesLoading: false
          });
        });
      }
    }));

    this.materializeAgent.connect();
  }

  ngOnDestroy() {
    this.stopMaterializeRecording();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  startMaterializeRecording() {
    this.recordingName = RandomUtils.randomString(30);
    this.allFeet = [];
    this.materializeAgent.startPlateDynamicRecording(this.recordingName)
        .catch((e) => {
          this.recordingName = undefined;
          this.toastControl.presentError(e, 'Démarrage de l\'enregistrement plateforme impossible')
        });
  }
  stopMaterializeRecording() {
    if (this.recordingName) {
      this.materializeAgent.stopPlateRecording(this.recordingName)
          .catch((e) => this.toastControl.presentError(e, 'Arrêt de l\'enregistrement plateforme impossible'))
          .finally(() => this.recordingName = undefined);
    }

  }

  selectUnselect(foot: MaterializePlateExamDynamicComponentFoot) {
    if (foot.selected === true) {
      foot.selected = false;
      foot.frames = undefined;
    } else {
      this.select(foot);
    }
  }

  select(foot: MaterializePlateExamDynamicComponentFoot) {
    const selectedFeet = this.selectedFeet;
    if (selectedFeet.length < 2) {
      if (selectedFeet.length === 1 && selectedFeet[0].type === foot.type) {
        this.toastControl.presentError(undefined, 'Veuillez sélectionner 1 pied gauche et un pied droit');
      } else {
        if (this.recordingName) {
          if (foot.frames?.length === 0) {
            foot.framesLoading = true;
            this.materializeAgent.fetchFootPressureRawData(this.recordingName, foot.index)
                .then((value: MaterializePlateFootRawData) => {
                  const mirrorXFrames = value.frames.map(value1 => {
                    return {
                      data: this.mirrorXFrame(value1.data)
                    };
                  });
                  foot.frames = mirrorXFrames;
                  this.plate = value.common.plate;
                  foot.selected = true;
                  if (this.selectedFeet?.length === 2) {
                    this.compare();
                  }

                })
                .catch((e) => this.toastControl.presentError(e, 'Récupération des données du pas impossible.'))
                .finally(() => foot.framesLoading = false)
          } else {
            foot.selected = true;
            if (this.selectedFeet?.length === 2) {
              this.compare();
            }
          }
        }
      }
    } else {
      this.toastControl.presentError(undefined, 'Vous ne pouvez comparer que 2 pieds à la fois');
    }
  }

  mirrorXFrame(data: number[][]): number[][] {
    if (!data || data.length === 0) {
      return [];
    }
    return data.map(row => [...row].reverse());
  }

  compare() {
    if (this.recordingName) {
      let selectedFeet: MaterializePlateExamDynamicComponentFoot[] = this.selectedFeet;
      if (selectedFeet.length == 2) {
        if (selectedFeet[0].type === 'right') {
          selectedFeet = [selectedFeet[1], selectedFeet[0]];
        }
        const input: MaterializePlateExamDynamicFootComparisonDialogInput = {
          feet: [{
           data: {
             frames: selectedFeet[0].frames,
             cellWidth: this.plate.sensorWidth,
             cellHeight: this.plate.sensorHeight,
           } ,
            picture: selectedFeet[0].picture,
            durationInMillis: selectedFeet[0].durationInMillis,
            type: 'left',
            drawableData: undefined
          },
            {
              data: {
                frames: selectedFeet[1].frames,
                cellWidth: this.plate.sensorWidth,
                cellHeight: this.plate.sensorHeight,
              } ,
              picture: selectedFeet[1].picture,
              durationInMillis: selectedFeet[1].durationInMillis,
              type: 'left',
              drawableData: undefined
            },
          ],
          frameRate: this.plate.frameRate,
          sensorWidth: this.plate.sensorWidth,
          sensorHeight: this.plate.sensorHeight,
        }
        this.dialogController.open(MaterializePlateExamDynamicFootComparisonDialogComponent, input, {
          title: 'Comparaison',
          width: '95%'
        }).afterClosed()
            .toPromise()
            .then((value: MaterializePlateExamDynamicFootComparisonDialogOutput) => {
              this.changedEvent.emit({
                left: {
                  image: value.left.originalPicture,
                  durationInMillis: value.left.durationInMillis,
                  pressureData: {
                    frames: value.left.pressureData.frames,
                    cellWidth: value.left.pressureData.cellWidth,
                    cellHeight: value.left.pressureData.cellHeight
                  },
                  gravityCenters: value.left.gravityCenters,
                  drawableData: value.left.drawableData
                },
                right: {
                  image: value.right.originalPicture,
                  durationInMillis: value.right.durationInMillis,
                  pressureData: {
                    frames: value.right.pressureData.frames,
                    cellWidth: value.right.pressureData.cellWidth,
                    cellHeight: value.right.pressureData.cellHeight
                  },
                  gravityCenters: value.right.gravityCenters,
                  drawableData: value.right.drawableData
                },
                plate: this.plate
              });
            });
      }
    }
  }


  get selectedFeet(): MaterializePlateExamDynamicComponentFoot[] {
    return this.allFeet.filter(value => value.selected === true);
  }
}

export class MaterializePlateExamDynamicComponentData {
  left: MaterializePlateExamDynamicComponentDataFoot;
  right: MaterializePlateExamDynamicComponentDataFoot;
  plate: MaterializePlateSummary;
}

export class MaterializePlateExamDynamicComponentDataFoot {
  image: string;
  pressureData: MaterializePlateExamDynamicComponentFootPressureData;
  gravityCenters: PressureGravityCenter[] = [];
  durationInMillis: number;
  drawableData: DrawingOnImageChangedEvent;

}

export class MaterializePlateExamDynamicComponentFoot {
  selected: boolean;
  durationInMillis: number;
  picture: string;
  type: 'left' | 'right';
  plateId: number;
  recordingName: string;
  index: number;
  frames: MaterializePlateFrame[];
  framesLoading: boolean;
}

export class MaterializePlateExamDynamicComponentFootPressureData {
  cellWidth: number;
  cellHeight: number;
  frames: MaterializePlateFrame[] = [];
}
